import { useQuery } from '@tanstack/react-query'
import {
  LineChart, Line, BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts'
import { adminFetch } from '../hooks/useAdminFetch'

interface MetricsData {
  adoption: {
    total_tenants: number
    activation_rate_pct: number
    activated_tenants: number
    feature_adoption: Array<{ feature: string; tenants: number; pct: number }>
    retention: { '30d': number; '60d': number; '90d': number }
  }
  system_health: {
    upload_success_rate_30d: Array<{ date: string; success_rate: number; total: number; completed: number }>
    top_ingestion_errors: Array<{ error: string; occurrences: number }>
  }
  ai_costs: {
    calls_per_day: Array<{
      date: string
      chat_calls: number
      narrative_calls: number
      total_calls: number
      tokens_total: number
      estimated_cost_usd: number
    }>
    total_calls_30d: number
    total_tokens_30d: number
    avg_tokens_per_call: number
    cost_usd_30d: number
    projected_monthly_cost_usd: number
  }
  business: {
    tenants_by_plan: Array<{ plan: string; count: number }>
    new_tenants_per_week: Array<{ week: string; new_tenants: number }>
  }
}

const PLAN_COLORS: Record<string, string> = {
  trial: '#eab308',
  starter: '#6366f1',
  growth: '#a855f7',
  enterprise: '#22c55e',
}
const CHART_COLORS = ['#6366f1', '#06b6d4', '#a855f7', '#f59e0b', '#22c55e', '#ef4444']

function KpiCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div
      className="rounded-xl border p-4"
      style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
    >
      <div className="text-xs mb-1" style={{ color: 'var(--km-text-muted)' }}>{label}</div>
      <div className="text-2xl font-bold" style={{ color: color ?? 'var(--km-text-primary)' }}>{value}</div>
      {sub && <div className="text-xs mt-0.5" style={{ color: 'var(--km-text-muted)' }}>{sub}</div>}
    </div>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3
      className="text-xs font-semibold uppercase tracking-wider mb-3"
      style={{ color: 'var(--km-text-muted)' }}
    >
      {children}
    </h3>
  )
}

const tooltipStyle = {
  contentStyle: {
    background: 'var(--km-surface)',
    border: '1px solid var(--km-border)',
    borderRadius: 8,
    fontSize: 12,
  },
  labelStyle: { color: 'var(--km-text-muted)' },
}

export function AdminMetrics() {
  const { data, isLoading, error } = useQuery<MetricsData>({
    queryKey: ['admin', 'metrics'],
    queryFn: () => adminFetch('/api/v1/admin/metrics'),
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-sm" style={{ color: 'var(--km-text-muted)' }}>Caricamento metriche…</p>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border p-5 text-sm" style={{ borderColor: 'var(--km-border)', color: 'var(--km-error)' }}>
        Errore nel caricamento delle metriche.
      </div>
    )
  }

  const { adoption, system_health, ai_costs, business } = data

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold" style={{ color: 'var(--km-text-primary)' }}>Metriche Prodotto</h2>
        <p className="text-sm mt-0.5" style={{ color: 'var(--km-text-muted)' }}>Adoption, salute sistema, AI & costi — ultimi 30 giorni</p>
      </div>

      {/* ---- ADOPTION ---- */}
      <div
        className="rounded-xl border p-5 space-y-5"
        style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
      >
        <SectionTitle>Adoption</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Tenant totali" value={adoption.total_tenants} />
          <KpiCard
            label="Activation rate"
            value={`${adoption.activation_rate_pct}%`}
            sub={`${adoption.activated_tenants} tenant attivati`}
            color="#22c55e"
          />
          <KpiCard
            label="Retention 30gg"
            value={adoption.retention['30d']}
            sub="tenant con ≥1 login"
          />
          <KpiCard
            label="Retention 90gg"
            value={adoption.retention['90d']}
            sub="tenant con ≥1 login"
          />
        </div>

        {/* Feature adoption bar chart */}
        <div>
          <div className="text-xs mb-3" style={{ color: 'var(--km-text-muted)' }}>Adozione per funzionalità</div>
          <div className="space-y-2">
            {adoption.feature_adoption.map((f) => (
              <div key={f.feature} className="flex items-center gap-3">
                <div className="w-28 text-xs text-right shrink-0" style={{ color: 'var(--km-text-secondary)' }}>
                  {f.feature}
                </div>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--km-border)' }}>
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${f.pct}%`, background: '#6366f1' }}
                  />
                </div>
                <div className="w-20 text-xs" style={{ color: 'var(--km-text-muted)' }}>
                  {f.tenants} ({f.pct}%)
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---- SALUTE SISTEMA ---- */}
      <div
        className="rounded-xl border p-5 space-y-5"
        style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
      >
        <SectionTitle>Salute Sistema</SectionTitle>

        {/* Upload success rate */}
        {system_health.upload_success_rate_30d.length > 0 ? (
          <div>
            <div className="text-xs mb-2" style={{ color: 'var(--km-text-muted)' }}>Upload success rate (ultimi 30gg)</div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={system_health.upload_success_rate_30d}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--km-text-muted)' }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: 'var(--km-text-muted)' }} axisLine={false} tickLine={false} width={32} unit="%" />
                <Tooltip {...tooltipStyle} formatter={(v: number) => [`${v}%`, 'Success rate']} />
                <Line type="monotone" dataKey="success_rate" stroke="#22c55e" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-sm" style={{ color: 'var(--km-text-muted)' }}>Nessun upload registrato negli ultimi 30 giorni.</p>
        )}

        {/* Top-10 errori */}
        {system_health.top_ingestion_errors.length > 0 && (
          <div>
            <div className="text-xs mb-2" style={{ color: 'var(--km-text-muted)' }}>Top errori ingestion (30gg)</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ color: 'var(--km-text-muted)' }}>
                    <th className="text-left py-1.5 pr-4 font-medium">#</th>
                    <th className="text-left py-1.5 pr-4 font-medium">Messaggio errore</th>
                    <th className="text-right py-1.5 font-medium">Occorrenze</th>
                  </tr>
                </thead>
                <tbody>
                  {system_health.top_ingestion_errors.map((e, i) => (
                    <tr key={i} className="border-t" style={{ borderColor: 'var(--km-border)' }}>
                      <td className="py-1.5 pr-4" style={{ color: 'var(--km-text-muted)' }}>{i + 1}</td>
                      <td className="py-1.5 pr-4 max-w-md truncate" style={{ color: 'var(--km-text-secondary)' }} title={e.error}>
                        {e.error}
                      </td>
                      <td className="py-1.5 text-right font-medium" style={{ color: '#ef4444' }}>
                        {e.occurrences}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ---- AI & COSTI ---- */}
      <div
        className="rounded-xl border p-5 space-y-5"
        style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
      >
        <SectionTitle>AI & Costi Anthropic</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Chiamate AI (30gg)" value={ai_costs.total_calls_30d.toLocaleString('it-IT')} />
          <KpiCard label="Token totali (30gg)" value={ai_costs.total_tokens_30d.toLocaleString('it-IT')} sub="input + output" />
          <KpiCard label="Token medi/chiamata" value={ai_costs.avg_tokens_per_call.toLocaleString('it-IT')} />
          <KpiCard
            label="Costo stimato (30gg)"
            value={`$${ai_costs.cost_usd_30d.toFixed(2)}`}
            sub={`Proiezione mensile: $${ai_costs.projected_monthly_cost_usd.toFixed(2)}`}
            color="#f59e0b"
          />
        </div>

        {ai_costs.calls_per_day.length > 0 && (
          <div>
            <div className="text-xs mb-2" style={{ color: 'var(--km-text-muted)' }}>Chiamate AI per giorno</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={ai_costs.calls_per_day}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--km-text-muted)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--km-text-muted)' }} axisLine={false} tickLine={false} width={28} />
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11, color: 'var(--km-text-muted)' }} />
                <Bar dataKey="narrative_calls" name="Narrativa" fill="#6366f1" stackId="a" radius={[0, 0, 0, 0]} />
                <Bar dataKey="chat_calls" name="Chat" fill="#06b6d4" stackId="a" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ---- BUSINESS ---- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Tenant per piano — donut */}
        <div
          className="rounded-xl border p-5"
          style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
        >
          <SectionTitle>Tenant per piano</SectionTitle>
          {business.tenants_by_plan.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={business.tenants_by_plan}
                  dataKey="count"
                  nameKey="plan"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={3}
                >
                  {business.tenants_by_plan.map((entry) => (
                    <Cell key={entry.plan} fill={PLAN_COLORS[entry.plan] ?? CHART_COLORS[0]} />
                  ))}
                </Pie>
                <Tooltip
                  {...tooltipStyle}
                  formatter={(v: number, name: string) => [v, name]}
                />
                <Legend
                  formatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                  wrapperStyle={{ fontSize: 12, color: 'var(--km-text-muted)' }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm py-8 text-center" style={{ color: 'var(--km-text-muted)' }}>Nessun dato.</p>
          )}
        </div>

        {/* Nuovi tenant per settimana */}
        <div
          className="rounded-xl border p-5"
          style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
        >
          <SectionTitle>Nuovi tenant per settimana (12 sett.)</SectionTitle>
          {business.new_tenants_per_week.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={business.new_tenants_per_week}>
                <XAxis
                  dataKey="week"
                  tick={{ fontSize: 9, fill: 'var(--km-text-muted)' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: string) => v.slice(5)} // MM-DD
                />
                <YAxis tick={{ fontSize: 10, fill: 'var(--km-text-muted)' }} axisLine={false} tickLine={false} width={24} />
                <Tooltip {...tooltipStyle} formatter={(v: number) => [v, 'Nuovi tenant']} />
                <Bar dataKey="new_tenants" name="Nuovi tenant" fill="#6366f1" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm py-8 text-center" style={{ color: 'var(--km-text-muted)' }}>Nessun dato.</p>
          )}
        </div>
      </div>
    </div>
  )
}
